import {
  HelpIcon,
  Icon,
  IconNames,
  Switch,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { useState } from 'react';

import styles from './presenceDropdownItem.module.scss';

type PresenceDropdownItemProps = {
  /** Icon to display in the item. */
  icon: IconNames;
  /** Text to display in the item. */
  text: string;
  /** Text for the tooltip displayed when hovering the whole item. */
  tooltipText?: string;
  /** Body text for the popover displayed when hovering an info icon. */
  helpText?: JSX.Element | string;
  /** Title for the popover displayed when hovering an info icon. */
  helpTitle?: string;
  /** Elements to render in the action area to the right of the item. */
  children?: JSX.Element;
  /** Function to call when the item is clicked. */
  onClick?: () => void;
  /** Subitems to render below the item. */
  subitems?: JSX.Element[];
  /** Whether the item is a subitem. */
  isSubitem?: boolean;
  /** An ID for the description element, to use in tests. */
  descriptionTestId?: string;
  /** Indicates if the item is disabled. */
  disabled?: boolean;
};

export const PresenceDropdownItem = ({
  icon,
  text,
  tooltipText,
  helpTitle,
  helpText,
  children,
  onClick,
  subitems,
  isSubitem = false,
  descriptionTestId,
  disabled = false,
}: PresenceDropdownItemProps): JSX.Element => {
  const [isSubchannelsOpen, setIsSubchannelsOpen] = useState(false);

  const content = (
    <li
      className={classNames(styles.item, {
        [styles.subitem]: isSubitem,
        [styles.disabled]: disabled,
      })}
      onClick={onClick}
    >
      <span className={styles.itemLabel}>
        <Icon className={styles.icon} name={icon} size={20} />
        <span className={styles.text} data-testid={descriptionTestId}>
          {text}
        </span>
        {helpText ? (
          <HelpIcon
            tooltipClassName={styles.helpPopover}
            title={helpTitle}
            text={helpText}
            placement="right"
          />
        ) : null}
      </span>
      <span className={styles.actionArea}>
        {children}
        {subitems && subitems.length > 0 && (
          <Icon
            name="angle-down"
            className={classNames(styles.subitemsIcon, {
              [styles.subitemsIconOpen]: isSubchannelsOpen,
            })}
            onClick={() => setIsSubchannelsOpen(!isSubchannelsOpen)}
          />
        )}
      </span>
    </li>
  );

  return tooltipText ? (
    <>
      <Tooltip content={tooltipText} dark>
        {content}
      </Tooltip>
      {isSubchannelsOpen && subitems}
    </>
  ) : (
    <>
      {content}
      {isSubchannelsOpen && subitems}
    </>
  );
};

type PresenceToggleProps = {
  /** Indicates if the toggle is active/checked or not. */
  active: boolean;
  /** Function to call when the toggle is clicked. */
  onToggle: () => void;
  /** Indicates if the agent is currently away, to style the color of the toggle. */
  isAway?: boolean;
  /** Text for the tooltip displayed when hovering the toggle button. */
  tooltipText?: string;
  /** An ID for the Switch element. Prefixed with `presence-toggle`. */
  id: string;
  /** Indicates if the toggle is disabled. */
  disabled?: boolean;
};

export const PresenceToggle = ({
  active,
  onToggle,
  isAway,
  tooltipText,
  id,
  disabled = false,
}: PresenceToggleProps): JSX.Element => {
  return (
    <Tooltip content={tooltipText} dark>
      <Switch
        id={`presence-toggle-${id.toLowerCase().replace(/\s/g, '-')}`}
        data-testid={`presence-toggle-${id.toLowerCase().replace(/\s/g, '-')}`}
        className={styles.presenceSwitch}
        variant={isAway ? 'pending' : 'active'}
        checked={active}
        onChange={onToggle}
        value=""
        name=""
        disabled={disabled}
      />
    </Tooltip>
  );
};

export const PresenceDropdownSeparator = (): JSX.Element => {
  return <hr className={styles.separator} />;
};
